import { isPlayerId } from '@/common/Types';
import { HttpGameShellTransport } from './GameShellHttpTransport';
/**
 * The server owns prompt tokens. This cache exists only because WaitingFor's
 * onsave hook is synchronous: main.ts primes it at startup, and submit outcomes
 * refresh it after the prompt token rotates.
 */
export class GameShellBootstrapCache {
    constructor(transport = new HttpGameShellTransport()) {
        this.transport = transport;
    }
    get(playerView) {
        const cached = this.cached;
        if (cached === undefined ||
            cached.playerId !== playerView.id ||
            cached.gameId !== playerView.game.gameId) {
            return undefined;
        }
        return {
            gameId: cached.gameId,
            baseRevision: cached.baseRevision,
            promptToken: cached.promptToken,
            engineVersion: cached.engineVersion,
        };
    }
    /** Called from main.ts when the player URL is already present. */
    async primeFromLocation() {
        if (typeof window === 'undefined') {
            return;
        }
        const playerId = new URLSearchParams(window.location.search).get('id');
        if (playerId === null || !isPlayerId(playerId)) {
            return;
        }
        await this.refreshById(playerId);
    }
    /** Refresh on a full authorized view/resync. */
    async refresh(playerView) {
        await this.refreshById(playerView.id);
    }
    /** Keep the next submit bound to the server-issued prompt token. */
    applyOutcome(outcome) {
        if (outcome.status !== 'applied' && outcome.status !== 'duplicate') {
            return;
        }
        const cached = this.cached;
        if (cached === undefined ||
            cached.playerId !== outcome.view.id ||
            cached.gameId !== outcome.view.game.gameId ||
            outcome.promptToken === undefined) {
            return;
        }
        this.cached = { ...cached, baseRevision: outcome.revision, promptToken: outcome.promptToken };
    }
    async refreshById(playerId) {
        if (this.inFlight !== undefined) {
            await this.inFlight;
            return;
        }
        this.inFlight = this.load(playerId).finally(() => {
            this.inFlight = undefined;
        });
        await this.inFlight;
    }
    async load(playerId) {
        let response;
        try {
            response = await this.transport.fetchBootstrap(playerId);
        }
        catch (_err) {
            this.cached = undefined;
            return;
        }
        if (response === undefined ||
            response.status !== 'ready' ||
            response.playerId !== playerId ||
            response.promptToken === undefined) {
            this.cached = undefined;
            return;
        }
        this.cached = {
            playerId,
            gameId: response.gameId,
            baseRevision: response.baseRevision,
            promptToken: response.promptToken,
            engineVersion: response.engineVersion,
        };
    }
}
let activeBootstrapCache;
export function installGameShellBootstrapProvider(bridge, transport = new HttpGameShellTransport()) {
    const cache = new GameShellBootstrapCache(transport);
    activeBootstrapCache = cache;
    bridge.setBootstrapProvider((view) => cache.get(view));
    bridge.setOutcomeListener?.((outcome) => cache.applyOutcome(outcome));
    return cache;
}
/** Refresh after a full authorized view or resync. */
export async function refreshGameShellBootstrap(playerView) {
    await activeBootstrapCache?.refresh(playerView);
}
